<!-- src/components/ri3/department/DepartmentList.vue -->
<template>
  <avs-card class="pb-4 department-list mt-2">
    <!-- ===== HEADER / TOOLBAR (même style que VoucherTable) ===== -->
    <template v-slot:title>
      <!-- Desktop -->
      <div v-if="$vuetify.breakpoint.mdAndUp">
        <v-container fluid class="py-2">
          <v-row>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="search"
                clearable
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-magnify"
                :label="$t('commons.search')"
              />
            </v-col>

            <v-col cols="12" md="4">
              <v-select
                v-model="statusFilter"
                :items="statusOptions"
                item-text="text"
                item-value="value"
                clearable
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-filter"
                :label="$t('Filtrer par statut')"
              />
            </v-col>

            <v-col cols="12" md="4" class="d-flex align-center justify-end">
              <avs-createButton
                :spantext="$t('actions.create', { entity: $t('commons.department').toLowerCase() })"
                menu
                :name="$t('actions.new', { entity: $t('commons.department').toLowerCase() })"
                @click="handleOpenEditDialog()"
              />
            </v-col>
          </v-row>
        </v-container>
      </div>

      <!-- Mobile -->
      <div v-else class="col-12 px-4 pt-2 mt-5">
        <v-text-field
          v-model="search"
          clearable
          outlined
          dense
          hide-details
          prepend-inner-icon="mdi-magnify"
          :label="$t('commons.search')"
          class="mb-3"
        />
        <v-select
          v-model="statusFilter"
          :items="statusOptions"
          item-text="text"
          item-value="value"
          clearable
          outlined
          dense
          hide-details
          prepend-inner-icon="mdi-filter"
          :label="$t('Filtrer par statut')"
          class="mb-3 mt-3"
        />
        <avs-createButton
          class="full-width"
          :spantext="$t('actions.create', { entity: $t('commons.department').toLowerCase() })"
          :name="$t('actions.new', { entity: $t('commons.department').toLowerCase() })"
          @click="handleOpenEditDialog()"
        />
      </div>
    </template>

    <!-- ===== Contenu ===== -->
    <template v-slot:content>
      <!-- Desktop : tableau -->
      <v-data-table
        v-if="$vuetify.breakpoint.mdAndUp"
        class="ma-6 bgTable"
        dense
        :headers="headers"
        hide-default-footer
        :no-data-text="$t('messages.information.no_data')"
        :items="filteredItems"
        :items-per-page="-1"
        item-key="id"
        @dblclick:row="(_, { item }) => tryOpenEdit(item)"
      >
        <template v-slot:item.parent="{ item }">
          {{ item.parent_object?.name || "-" }}
        </template>

        <template v-slot:item.budget="{ item }">
          {{ formatNumber(item.budget) }} XOF
        </template>

        <template v-slot:item.period_total_amount="{ item }">
          {{ formatNumber(item.period_total_amount) }} XOF
        </template>

        <template v-slot:item.period_remaining_amount="{ item }">
          {{ item.period_remaining_amount == null ? '-' : (formatNumber(item.period_remaining_amount) + ' XOF') }}
        </template>

        <template v-slot:item.period="{ item }">
          <span v-if="item.period_start && item.period_end">
            {{ toDate(item.period_start) }} → {{ toDate(item.period_end) }}
          </span>
          <span v-else>—</span>
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="d-flex justify-center align-center">
            <avs-button
              icon
              :iconI="'mdi-history'"
              label
              fab
              small
              :spantext="$t('Historique des budgets')"
              @click.stop="openBudgetHistory(item)"
            />

            <avs-editButton @click.stop="tryOpenEdit(item)" />
            <template v-if="isLocked(item)">
              <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on">
                    <avs-deleteButton :disabled="true" />
                  </div>
                </template>
                <span>{{ lockReason(item) || $t('Rattaché à des ressources') }}</span>
              </v-tooltip>
            </template>
            <template v-else>
              <avs-deleteButton @click.stop="tryOpenDelete(item)" />
            </template>
          </div>
        </template>
      </v-data-table>

      <!-- Mobile : cartes -->
      <div v-else class="px-4 pt-2">
        <v-alert
          v-if="!filteredItems.length"
          type="info"
          dense
          outlined
          class="mb-2"
        >
          {{ $t('messages.information.no_data') }}
        </v-alert>

        <v-card
          v-for="it in filteredItems"
          :key="it.id"
          class="mb-3 dept-card"
          outlined
          @click="tryOpenEdit(it)"
        >
          <v-card-title class="py-3">
            <div class="title-wrap">
              <div class="dept-name text-truncate">{{ it.name }}</div>
              <div class="dept-meta">
                <span class="grey--text text--darken-1">
                  {{ $t('Parent') }}: {{ it.parent_object?.name || '—' }}
                </span>
              </div>
            </div>
            <div class="ml-auto d-flex align-center">
              <v-chip
                small
                label
                :color="isLocked(it) ? 'error' : 'success'"
                text-color="white"
                class="text-capitalize"
              >
                {{ isLocked(it) ? $t('Verrouillé') : $t('Libre') }}
              </v-chip>
            </div>
          </v-card-title>

          <v-divider />

          <v-card-text class="py-3">
            <div class="grid-2">
              <div class="cell">
                <div class="label">{{ $t('Budget') }}</div>
                <div class="value">{{ formatNumber(it.budget) }} XOF</div>
              </div>

              <div class="cell">
                <div class="label">{{ $t('Période') }}</div>
                <div class="value">
                  <template v-if="it.period_start && it.period_end">
                    {{ toDate(it.period_start) }} → {{ toDate(it.period_end) }}
                  </template>
                  <template v-else>—</template>
                </div>
              </div>

              <div class="cell">
                <div class="label">{{ $t('Véhicules') }}</div>
                <div class="value">{{ it.vehicles_count || 0 }}</div>
              </div>

              <div class="cell">
                <div class="label">{{ $t('Staff') }}</div>
                <div class="value">{{ it.staff_count || 0 }}</div>
              </div>
            </div>
          </v-card-text>

          <v-divider />

          <v-card-actions class="py-2">
            <v-btn
              small
              text
              color="primary"
              @click.stop="openBudgetHistory(it)"
            >
              <v-icon left small>mdi-history</v-icon>{{ $t('Historique') }}
            </v-btn>

            <v-btn small text color="primary" @click.stop="tryOpenEdit(it)">
              <v-icon left small>mdi-pencil</v-icon>{{ $t('Modifier') }}
            </v-btn>

            <v-spacer />

            <template v-if="isLocked(it)">
              <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    <v-btn small text color="error" disabled>
                      <v-icon left small>mdi-delete</v-icon>{{ $t('Supprimer') }}
                    </v-btn>
                  </span>
                </template>
                <span>{{ lockReason(it) || $t('Rattaché à des ressources') }}</span>
              </v-tooltip>
            </template>
            <template v-else>
              <v-btn small text color="error" @click.stop="tryOpenDelete(it)">
                <v-icon left small>mdi-delete</v-icon>{{ $t('Supprimer') }}
              </v-btn>
            </template>
          </v-card-actions>
        </v-card>
      </div>

      <!-- Dialog édition -->
      <v-dialog
        v-model="editDialogIsOpen"
        :fullscreen="$vuetify.breakpoint.smAndDown"
        :max-width="$vuetify.breakpoint.smAndDown ? '100vw' : '900px'"
        scrollable
        transition="dialog-bottom-transition"
      >
        <component
          v-if="editDialogContent"
          v-model="selectedItem"
          :is="editDialogContent"
          :hasUniqueName="hasUniqueName"
          :isEditable="isEditable"
          @cancel="handleCancel"
          @error="handleError"
          @success="handleSuccess"
        />
      </v-dialog>

      <!-- Dialog historique budget -->
      <v-dialog
        v-model="budgetHistoryDialogIsOpen"
        :fullscreen="$vuetify.breakpoint.smAndDown"
        :max-width="$vuetify.breakpoint.smAndDown ? '100vw' : '700px'"
        scrollable
      >
        <v-card>
          <v-card-title>
            <span class="text-h6">
              {{ $t('Historique du budget') }}
              <span v-if="budgetHistoryTarget"> — {{ budgetHistoryTarget.name }}</span>
            </span>
            <v-spacer />
            <v-btn icon @click="budgetHistoryDialogIsOpen = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-divider />

          <v-card-text>
            <div v-if="budgetHistoryLoading" class="text-center py-6">
              <v-progress-circular indeterminate color="primary" />
            </div>

            <div v-else>
              <v-alert
                v-if="!budgetHistoryItems.length"
                type="info"
                color="secondary"
                class="mt-2"
                dense
                outlined
              >
                {{ $t('Aucun historique de budget pour ce département.') }}
              </v-alert>

              <v-data-table
                v-else
                :headers="budgetHistoryHeaders"
                :items="budgetHistoryItems"
                dense
                hide-default-footer
                disable-pagination
              >
                <template v-slot:item.old_budget="{ item }">
                  {{ item.old_budget === null ? '—' : (formatNumber(item.old_budget) + ' XOF') }}
                </template>

                <template v-slot:item.new_budget="{ item }">
                  {{ formatNumber(item.new_budget) }} XOF
                </template>

                <template v-slot:item.changed_at="{ item }">
                  {{ toDateTime(item.changed_at) }}
                </template>
              </v-data-table>
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>

      <DeleteDialog
        v-model="deleteDialogIsOpen"
        @cancel="handleCancel"
        @delete="handleDelete"
        resource="Department"
      />
    </template>
  </avs-card>
</template>

<script>
import DeleteDialog from "@/components/generics/dialogs/DeleteDialog.vue"
import DepartmentEditor from "@/components/ri3/department/DepartmentEditor.vue"
import { DepartmentService } from "@/services"
import { mkUnique } from "@/utils/validator"

export default {
  name: "DepartmentList",
  components: { DeleteDialog, DepartmentEditor },
  data() {
    return {
      search: "",
      items: [],
      statusFilter: null, // 'locked' | 'unlocked' | null
      statusOptions: [
        { text: this.$t("Tous"), value: null },
        { text: this.$t("Libre"), value: "unlocked" },
        { text: this.$t("Verrouillé"), value: "locked" },
      ],
      headers: [
        { text: this.$t("commons.name"), value: "name" },
        { text: this.$t("Parent"), value: "parent" },
        { text: this.$t("Budget"), value: "budget", align: "right" },
        { text: this.$t("Période"), value: "period" },
        { text: this.$t("Véhicules"), value: "vehicles_count", align: "center" },
        { text: this.$t("Staff"), value: "staff_count", align: "center" },
        { text: "actions", value: "actions", align: "center", sortable: false },
      ],
      editDialogIsOpen: false,
      editDialogContent: null,
      deleteDialogIsOpen: false,
      selectedItem: null,
      isEditable: true,

      // Historique budget
      budgetHistoryDialogIsOpen: false,
      budgetHistoryItems: [],
      budgetHistoryLoading: false,
      budgetHistoryTarget: null,
      budgetHistoryHeaders: [
        { text: "Ancien budget", value: "old_budget", align: "right" },
        { text: "Nouveau budget", value: "new_budget", align: "right" },
        { text: "Date", value: "changed_at" },
        { text: "Modifié par", value: "changed_by" },
      ],
    }
  },
  computed: {
    company() {
      return this.$store.state.app.company
    },
    hasUniqueName() {
      const names = this.items
        .filter(e => e.name !== this.selectedItem?.name)
        .map(e => e.name)
      return mkUnique(names, "name")
    },
    filteredItems() {
      const q = (this.search || "").toLowerCase()
      let list = this.items

      if (this.statusFilter === "locked") list = list.filter(it => this.isLocked(it))
      else if (this.statusFilter === "unlocked") list = list.filter(it => !this.isLocked(it))

      return list.filter(it => {
        const parent = it.parent_object?.name || ""
        return (
          (it.name || "").toLowerCase().includes(q) ||
          parent.toLowerCase().includes(q)
        )
      })
    },
  },
  created() {
    this.fetchDepartments()
  },
  methods: {
    fetchDepartments() {
      DepartmentService.getDepartment(this.company.pk)
        .then(res => (this.items = res || []))
        .catch(err => {
          if (err?.response?.status === 403) {
            this.$store.commit("app/message", {
              msg: this.$t("messages.error.permission_denied"),
              color: "error",
            })
          }
        })
    },
    toDate(v) {
      if (!v) return "-"
      try {
        const d = new Date(v)
        const yyyy = d.getFullYear()
        const mm = String(d.getMonth() + 1).padStart(2, "0")
        const dd = String(d.getDate()).padStart(2, "0")
        return `${dd}/${mm}/${yyyy}`
      } catch {
        return "-"
      }
    },
    toDateTime(v) {
      if (!v) return "-"
      try {
        const d = new Date(v)
        const yyyy = d.getFullYear()
        const mm = String(d.getMonth() + 1).padStart(2, "0")
        const dd = String(d.getDate()).padStart(2, "0")
        const hh = String(d.getHours()).padStart(2, "0")
        const mi = String(d.getMinutes()).padStart(2, "0")
        return `${dd}/${mm}/${yyyy} ${hh}:${mi}`
      } catch {
        return "-"
      }
    },
    formatNumber(value) {
      if (value === null || value === undefined || value === "") return "0"
      const n = Number(value)
      if (Number.isNaN(n)) return "0"
      const [intPart, decPartRaw] = n.toFixed(2).split(".")
      const intFmt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
      return decPartRaw === "00" ? intFmt : `${intFmt},${decPartRaw}`
    },
    isLocked(item) {
      if (typeof item.is_locked === "boolean") return item.is_locked
      const vc = Number(item.vehicles_count || 0)
      const sc = Number(item.staff_count || 0)
      return vc > 0 || sc > 0
    },
    lockReason(item) {
      return (
        item.locked_reason ||
        [
          (item.vehicles_count || 0) > 0 ? `${item.vehicles_count} véhicule(s)` : null,
          (item.staff_count || 0) > 0 ? `${item.staff_count} staff` : null,
        ]
          .filter(Boolean)
          .join(" · ")
      )
    },
    tryOpenEdit(item) {
      this.handleOpenEditDialog(item)
    },
    tryOpenDelete(item) {
      if (this.isLocked(item)) {
        this.$store.commit("app/message", {
          msg: this.$t("Ce département est rattaché à des ressources et ne peut pas être supprimé."),
          color: "warning",
        })
        return
      }
      this.handleOpenDeleteDialog(item)
    },
    handleOpenEditDialog(item) {
      this.selectedItem = item ? JSON.parse(JSON.stringify(item)) : null
      this.editDialogIsOpen = true
      this.editDialogContent = "DepartmentEditor"
    },
    handleOpenDeleteDialog(item) {
      this.selectedItem = item
      this.deleteDialogIsOpen = true
    },
    async openBudgetHistory(item) {
      this.budgetHistoryTarget = item
      this.budgetHistoryDialogIsOpen = true
      this.budgetHistoryLoading = true
      this.budgetHistoryItems = []

      try {
        const data = await DepartmentService.getBudgetHistory(this, item.id)
        this.budgetHistoryItems = data.results || []
      } catch (e) {
        this.$store.commit("app/message", {
          msg: this.$t("messages.error.operation_failed"),
          color: "error",
        })
      } finally {
        this.budgetHistoryLoading = false
      }
    },
    handleDelete() {
      if (!this.selectedItem) return
      DepartmentService.delete(this.selectedItem.id)
        .then(() => this.handleSuccess())
        .catch(error => {
          const data = error?.response?.data
          const msg =
            data?.detail ||
            data?.non_field_errors?.[0] ||
            data?.state ||
            this.$t("messages.error.operation_failed")
          this.handleError(msg)
        })
    },
    handleCancel() {
      this.selectedItem = null
      this.editDialogIsOpen = false
      this.editDialogContent = null
      this.deleteDialogIsOpen = false
      this.budgetHistoryDialogIsOpen = false
      this.budgetHistoryTarget = null
      this.budgetHistoryItems = []
    },
    handleError(message) {
      this.$store.commit("app/message", { msg: message, color: "error" })
    },
    handleSuccess() {
      this.handleCancel()
      this.$nextTick(() => {
        this.fetchDepartments()
        this.$store.commit("app/message", {
          msg: this.$t("messages.success.operation"),
          color: "success",
        })
      })
    },
  },
}
</script>

<style scoped>
/* Bouton créer pleine largeur en mobile */
.full-width {
  width: 100%;
}

/* Carte mobile */
.dept-card {
  border-radius: 12px;
  box-shadow: 0 6px 18px rgba(16, 24, 40, 0.06);
}
.title-wrap {
  min-width: 0;
}
.dept-name {
  font-weight: 700;
  font-size: 16px;
  max-width: 70vw;
}
.dept-meta {
  font-size: 12px;
  margin-top: 2px;
}

/* Grille 2 colonnes dans le body de la carte */
.grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 8px 16px;
}
.cell .label {
  font-size: 12px;
  color: #6b7280;
}
.cell .value {
  font-size: 14px;
  font-weight: 600;
}

/* Très petits écrans : 1 colonne */
@media (max-width: 360px) {
  .grid-2 {
    grid-template-columns: 1fr;
  }
}
</style>
